/**
 * Messpunkte für Google Analytics 4 und Google Ads.
 *
 * Zwei Ereignisse, die Google Tag Manager von allein nicht sehen kann, weil
 * sie ohne Seitenwechsel passieren:
 *
 *  - `generate_lead`       – Kontaktformular erfolgreich abgeschickt. Der Name
 *                            ist ein von GA4 empfohlenes Ereignis; so lässt er
 *                            sich ohne Umbenennung als Conversion in Google Ads
 *                            importieren.
 *  - `phone_click`         – Klick auf eine Telefonnummer, irgendwo auf der
 *                            Seite. Als Conversion in Google Ads geeignet.
 *  - `nis2_check_complete` – Betroffenheitscheck bis zum Ergebnis ausgefüllt.
 *                            Laut Playbook nur zur Beobachtung, nicht für Gebote.
 *
 * Datenschutz: Hier wird nur ein Eintrag in ein lokales Array geschrieben.
 * Übertragen wird nichts – GTM wird erst nach Einwilligung geladen
 * (siehe components/analytics/Analytics.tsx), und Consent Mode v2 steht bis
 * dahin auf "denied". Personenbezogene Angaben (Name, E-Mail, Nachricht)
 * werden bewusst NIE mitgegeben, nur die Kategorie der Anfrage.
 *
 * Im Tag Manager braucht jedes Ereignis einen Trigger vom Typ
 * "Benutzerdefiniertes Ereignis" mit genau diesem Namen.
 */

import { gtag } from "@/lib/consent";
import { publicEnv } from "@/lib/env";

type Wert = string | number | boolean;

export const TRACKING_EVENTS = {
  lead: "generate_lead",
  nis2Check: "nis2_check_complete",
  /**
   * Klick auf eine Telefonnummer. Für Handwerks- und
   * Dienstleistungsbetriebe oft der wichtigere Weg als das Formular –
   * ohne dieses Ereignis optimiert Google Ads auf die halbe Wahrheit.
   * Ausgelöst von components/analytics/AnrufTracking.tsx.
   */
  anruf: "phone_click",
} as const;

export function trackEvent(event: string, params: Record<string, Wert> = {}) {
  if (typeof window === "undefined") return;
  // Genau EIN Weg, festgelegt beim Build – sonst zählt ein GA4-Ereignis-Tag
  // in GTM und ein direkter gtag-Aufruf dieselbe Anfrage doppelt.
  if (publicEnv.gtmId) {
    // Google Tag Manager: benutzerdefiniertes Ereignis, Trigger hören darauf.
    // Mehr nicht. Hier stand bis zum 22.09.2026 zusätzlich ein gtag-Aufruf mit
    // `send_to` auf dieselbe Property, die auch im Container hängt — damit kam
    // jede Anfrage zweimal in GA4 an und wäre in Google Ads doppelt als
    // Conversion gezählt worden.
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, ...params });
    return;
  }
  // GA4 direkt über gtag.js: gtag wertet nur eigene Befehle aus, keine
  // Objekte im dataLayer. Vor der Einwilligung bleibt der Befehl in der
  // Warteschlange und wird nur gesendet, wenn später zugestimmt wird.
  gtag("event", event, params);
}
